"use client"

import { useQuery } from "@tanstack/react-query"
import apiClient, { getApiBaseUrl } from "@/lib/api/api-client"
import { toCamelCase } from "@/lib/case-utils"
import type { RosterPlayerDetail } from "@/types/roster-types"

export const rosterKeys = {
  all: ["roster"] as const,
  list: () => [...rosterKeys.all, "list"] as const,
  detail: (slug: string) => [...rosterKeys.all, "detail", slug] as const,
  shared: (shareToken: string) => [...rosterKeys.all, "shared", shareToken] as const,
  adminDetail: (id: string) => [...rosterKeys.all, "admin", id] as const,
}

export type PublicRosterView = {
  locked: boolean
  player: RosterPlayerDetail | null
}

export function usePublishedRoster() {
  return useQuery({
    queryKey: rosterKeys.list(),
    queryFn: () => apiClient.roster.listPublic(),
    staleTime: 60_000,
  })
}

async function fetchSharedRoster(shareToken: string): Promise<PublicRosterView> {
  const response = await fetch(
    `${getApiBaseUrl()}/api/public/roster/s/${encodeURIComponent(shareToken)}`,
    { credentials: "include" }
  )
  if (response.status === 403) {
    return { locked: true, player: null }
  }
  if (!response.ok) {
    throw new Error("Player not found")
  }
  const player = toCamelCase(await response.json()) as RosterPlayerDetail
  return { locked: false, player }
}

export function usePublishedRosterPlayer(opts: {
  slug?: string
  shareToken?: string
}) {
  const shareToken = opts.shareToken
  const slug = opts.slug
  return useQuery({
    queryKey: shareToken ? rosterKeys.shared(shareToken) : rosterKeys.detail(slug ?? ""),
    queryFn: async (): Promise<PublicRosterView> => {
      if (shareToken) return fetchSharedRoster(shareToken)
      const player = await apiClient.roster.getPublic(slug!)
      return { locked: false, player }
    },
    enabled: Boolean(shareToken || slug),
    staleTime: shareToken ? 0 : 60_000,
    gcTime: shareToken ? 0 : 60_000,
    refetchOnMount: shareToken ? "always" : true,
    refetchOnWindowFocus: !shareToken,
    retry: (count) => !shareToken && count < 2,
  })
}

export function useAdminRosterPlayer(playerId: string | undefined) {
  return useQuery({
    queryKey: rosterKeys.adminDetail(playerId ?? ""),
    queryFn: () => apiClient.roster.getAdmin(playerId!),
    enabled: Boolean(playerId),
    staleTime: 15_000,
  })
}
